/**
 * Service for managing promotions (backoffice CRUD + customer carousel).
 */

import { apiClient, getAuthHeaders } from './api';
import { API_BASE_URL } from '../config/constants';

const getBackofficeHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('backoffice_access_token')}`,
});

export interface PromotionBanner {
  id: string;
  title: string;
  subtitle: string | null;
  image_url: string;
  image_path: string;
  image_name: string;
  cta_text: string | null;
  cta_url: string | null;
  sort_order: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  discount_percent: number;
  product_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface PromotionProductItem {
  id: string;
  promotion_id: string;
  product_id: string;
  discount_percent: number | null;
  effective_discount: number;
  product_name: string | null;
  product_part_number: string | null;
  product_family: string | null;
  created_at: string | null;
}

export interface PromotionProductsResponse {
  success: boolean;
  promotion_id: string;
  discount_percent: number;
  products: PromotionProductItem[];
  product_count: number;
}

export interface AddProductsResult {
  success: boolean;
  message: string;
  added: number;
  skipped: number;
  conflicts: Array<{
    product_id: string;
    product_name: string | null;
    existing_promotion: string | null;
  }>;
}

function resolveImageUrl(promo: PromotionBanner): PromotionBanner {
  if (promo.image_url && !promo.image_url.startsWith('http')) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    promo.image_url = `${baseUrl}${promo.image_url}`;
  }
  return promo;
}

class PromotionService {
  // ── Backoffice ──

  async listPromotions(
    page = 1,
    size = 20,
    activeOnly = false,
  ): Promise<{
    items: PromotionBanner[];
    total_count: number;
    page: number;
    size: number;
    total_pages: number;
  }> {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (activeOnly) params.set('active_only', 'true');

    const res = await fetch(
      `${API_BASE_URL}/backoffice/promotions?${params}`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed to list promotions: ${res.status}`);
    const data = await res.json();
    data.items = data.items.map(resolveImageUrl);
    return data;
  }

  async createPromotion(
    data: {
      title: string;
      subtitle?: string;
      cta_text?: string;
      cta_url?: string;
      sort_order?: number;
      is_active?: boolean;
      starts_at?: string;
      ends_at?: string;
      discount_percent?: number;
    },
    file: File,
  ): Promise<PromotionBanner> {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.subtitle) formData.append('subtitle', data.subtitle);
    if (data.cta_text) formData.append('cta_text', data.cta_text);
    if (data.cta_url) formData.append('cta_url', data.cta_url);
    formData.append('sort_order', String(data.sort_order ?? 0));
    formData.append('is_active', String(data.is_active ?? true));
    if (data.starts_at) formData.append('starts_at', data.starts_at);
    if (data.ends_at) formData.append('ends_at', data.ends_at);
    if (data.discount_percent !== undefined) formData.append('discount_percent', String(data.discount_percent));
    formData.append('file', file);

    const res = await fetch(`${API_BASE_URL}/backoffice/promotions/`, {
      method: 'POST',
      headers: getBackofficeHeaders(),
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Create failed: ${res.status}`);
    }
    const result = await res.json();
    return resolveImageUrl(result.promotion);
  }

  async updatePromotion(
    id: string,
    data: {
      title?: string;
      subtitle?: string;
      cta_text?: string;
      cta_url?: string;
      sort_order?: number;
      is_active?: boolean;
      starts_at?: string;
      ends_at?: string;
      discount_percent?: number;
    },
    file?: File,
  ): Promise<PromotionBanner> {
    const formData = new FormData();
    if (data.title !== undefined) formData.append('title', data.title);
    if (data.subtitle !== undefined) formData.append('subtitle', data.subtitle);
    if (data.cta_text !== undefined) formData.append('cta_text', data.cta_text);
    if (data.cta_url !== undefined) formData.append('cta_url', data.cta_url);
    if (data.sort_order !== undefined) formData.append('sort_order', String(data.sort_order));
    if (data.is_active !== undefined) formData.append('is_active', String(data.is_active));
    if (data.starts_at !== undefined) formData.append('starts_at', data.starts_at);
    if (data.ends_at !== undefined) formData.append('ends_at', data.ends_at);
    if (data.discount_percent !== undefined) formData.append('discount_percent', String(data.discount_percent));
    if (file) formData.append('file', file);

    const res = await fetch(`${API_BASE_URL}/backoffice/promotions/${id}`, {
      method: 'PUT',
      headers: getBackofficeHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    const result = await res.json();
    return resolveImageUrl(result.promotion);
  }

  async deletePromotion(id: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/backoffice/promotions/${id}`, {
      method: 'DELETE',
      headers: getBackofficeHeaders(),
    });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  }

  // ── Promotion Products (backoffice) ──

  async getPromotionProducts(promoId: string): Promise<PromotionProductsResponse> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/promotions/${promoId}/products`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  }

  async addProductsToPromotion(
    promoId: string,
    data: {
      family_code?: string;
      subfamily_code?: string;
      product_ids?: string[];
      discount_percent?: number | null;
    },
  ): Promise<AddProductsResult> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/promotions/${promoId}/products`,
      {
        method: 'POST',
        headers: { ...getBackofficeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Failed: ${res.status}`);
    }
    return res.json();
  }

  async removeProductFromPromotion(promoId: string, productId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/promotions/${promoId}/products/${productId}`,
      { method: 'DELETE', headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
  }

  async removeAllProductsFromPromotion(promoId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/promotions/${promoId}/products`,
      { method: 'DELETE', headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
  }

  // ── Customer-facing ──

  async getActivePromotions(): Promise<PromotionBanner[]> {
    const res = await apiClient.get<{
      success: boolean;
      promotions: PromotionBanner[];
    }>('/promotions/active', getAuthHeaders());
    return (res.promotions || []).map(resolveImageUrl);
  }
}

export const promotionService = new PromotionService();
